import PropTypes from "prop-types";
import React, { useState } from "react";

import { toast } from "react-toastify";
import Popup from "reactjs-popup";
import { EditorState } from "draft-js";
import { richTextToHtml, richTextContentFromHtml } from '../../shared/utils/richTextHtml';
import Select from "react-select";
import { DndContext, KeyboardSensor, PointerSensor, closestCenter, useSensor, useSensors } from "@dnd-kit/core";
import {
  SortableContext,
  rectSortingStrategy,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";

import PageSectionMenu from "../../ConfirmShow/components/PageSectionMenu";
import RichEditor from "../../RichEditor/components/RichEditor";
import SetPermissionsPopup from "../../SetPermissionsPopup/components/SetPermissionsPopup";
import usePermissionSet from "../../EventPage/hooks/usePermissionSet";
import useNotes, { toastError } from "../hooks/useNotes";
import NoteAttachments from "./NoteAttachments";
import SortableNote from "./SortableNote";
import "../styles/notes.css";

const axios = require("axios").default;

const modalContentStyle = {
  textAlign: "left",
  border: "none",
  borderRadius: "8px",
  boxShadow: "var(--sl-shadow-sm)",
  background: "var(--sl-bg-primary)",
  width: "1140px",
  padding: "15px",
};

const popupContentStyle = {
  border: "1px solid var(--sl-border-secondary)",
  borderRadius: "8px",
  boxShadow: "var(--sl-shadow-sm)",
  background: "var(--sl-bg-primary)",
  width: "175px",
  padding: "0px",
};

const deleteModalContentStyle = {
  textAlign: "left",
  border: "none",
  borderRadius: "8px",
  boxShadow: "var(--sl-shadow-sm)",
  background: "var(--sl-bg-primary)",
  width: "500px",
  padding: "15px",
};

const createNoteTemplate = (csrfToken, team, openNote, successCallback) => {
  axios.defaults.headers.common["X-CSRF-TOKEN"] = csrfToken;

  axios
    .post(`/teams/${team.id}/note_templates`, {
      note_template: {
        title: openNote.title,
        body: openNote.body || "",
      },
    })
    .then((response) => {
      successCallback(response.data);
    })
    .catch(toastError);
};

const updateNoteTemplate = (csrfToken, team, openNote, successCallback) => {
  axios.defaults.headers.common["X-CSRF-TOKEN"] = csrfToken;

  axios
    .patch(`/teams/${team.id}/note_templates/${openNote.note_template_id}`, {
      note_template: {
        title: openNote.title,
        body: openNote.body || "",
      },
    })
    .then((response) => {
      successCallback(response.data);
    })
    .catch(toastError);
};

const deleteNoteTemplate = (csrfToken, team, noteTemplate, successCallback) => {
  axios.defaults.headers.common["X-CSRF-TOKEN"] = csrfToken;

  axios
    .delete(`/teams/${team.id}/note_templates/${noteTemplate.id}`)
    .then((data) => {
      successCallback(data);
    })
    .catch(toastError);
};

const formatOptionLabel = ({ value, label }, { context }, onClick) => (
  <div className="sl-note-template-option">
    <p className="sl-note-template-option-label" title={label}>
      {label}
    </p>
    {context === "menu" ? (
      <a
        href="#"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();

          if (window.confirm('Are you sure? Delete "' + label + '"?')) {
            onClick();
          }
        }}
        className="sl-note-template-option-delete"
      >
        <i className="fas fa-trash"></i>
      </a>
    ) : null}
  </div>
);

const TEMPLATE_SELECT_WIDTH = "260px";

const selectStyles = {
  container: (base, state) => ({
    ...base,
    opacity: state.isDisabled ? ".5" : "1",
    width: TEMPLATE_SELECT_WIDTH,
    zIndex: "999",
  }),
  placeholder: (styles) => ({
    ...styles,
    color: "var(--sl-text-placeholder)",
    fontWeight: "normal",
  }),
  control: (styles, state) => ({
    ...styles,
    minHeight: "40px",
    backgroundColor: "var(--sl-bg-primary)",
    border: `1px solid ${state.isFocused ? "var(--sl-border-brand)" : "var(--sl-border-secondary)"}`,
    borderRadius: "8px",
    boxShadow: state.isFocused ? "0 0 0 4px var(--sl-bg-brand-secondary)" : "var(--sl-shadow-xs)",
    "&:hover": {
      borderColor: state.isFocused ? "var(--sl-border-brand)" : "var(--sl-border-primary)",
    },
  }),
  valueContainer: (styles) => ({
    ...styles,
    padding: "2px 12px",
  }),
  singleValue: (styles) => ({
    ...styles,
    color: "var(--sl-text-primary)",
    fontWeight: "normal",
  }),
  input: (styles) => ({
    ...styles,
    color: "var(--sl-text-primary)",
  }),
  indicatorSeparator: () => ({ display: "none" }),
  dropdownIndicator: (styles) => ({
    ...styles,
    color: "var(--sl-text-tertiary)",
    "&:hover": {
      color: "var(--sl-text-secondary)",
    },
  }),
  menu: (provided) => ({
    ...provided,
    width: TEMPLATE_SELECT_WIDTH,
    backgroundColor: "var(--sl-bg-primary)",
    border: "1px solid var(--sl-border-secondary)",
    borderRadius: "8px",
    boxShadow: "var(--sl-shadow-sm)",
    overflow: "hidden",
    zIndex: "9999",
  }),
  menuList: (provided) => ({
    ...provided,
    padding: "4px",
  }),
  option: (provided, state) => ({
    ...provided,
    color: "var(--sl-text-primary)",
    backgroundColor: state.isFocused ? "var(--sl-bg-primary-hover)" : "var(--sl-bg-primary)",
    fontWeight: "normal",
    borderRadius: "6px",
    padding: "0px",
    cursor: "pointer",
  }),
};

const canSubmitForm = (note, original, isLoading) => {
  if (isLoading) {
    return false;
  }

  var noteBody;
  if (note.editorState) {
    noteBody = richTextToHtml(note.editorState.getCurrentContent());
  }

  var originalNoteBody;
  if (original.editorState) {
    originalNoteBody = richTextToHtml(original.editorState.getCurrentContent());
  }

  return (
    note &&
    note.title &&
    note.title.length > 0 &&
    (!note.id ||
      note.title !== original.title ||
      noteBody !== originalNoteBody ||
      note.note_template_id != original.note_template_id)
  );
};

const canSubmitTemplate = (note) => {
  return note && note.title && note.title.length > 0;
};

const Notes = ({
  initialData,
  csrfToken,
  team,
  confirm,
  currentUser,
  canManageNotes,
  canReadNotes,
  canManagePermissions,
  canManageNoteTemplates,
  guestTeamMemberships,
  promotersWithPermission,
  onNotesChanged,
}) => {
  const {
    notes,
    noteTemplates,
    notesPageSection,
    openNote,
    openNoteOriginal,
    openNoteLoading,
    openNoteChanged,
    openNoteTitleChanged,
    disableOpenNotePreviewMode,
    noteTemplatesChanged,
    noteUpdated,
    updateConfirmPageSection,
    fetchNewNote,
    closeOpenNote,
    createNote,
    deleteNote,
    fetchNoteToEdit,
    fetchNoteToPreview,
    updateNote,
    moveNote,
  } = useNotes(initialData, { team, csrfToken, confirm, onNotesChanged });

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const noteTitle = (id) => notes.find((note) => note.id === id)?.title ?? "a note";
  const reorderAnnouncements = {
    onDragStart: ({ active }) => `Picked up ${noteTitle(active.id)}.`,
    onDragOver: ({ active, over }) => (over ? `${noteTitle(active.id)} is over ${noteTitle(over.id)}.` : undefined),
    onDragEnd: ({ active, over }) => {
      if (!over) return `${noteTitle(active.id)} was dropped.`;
      if (active.id === over.id) return `${noteTitle(active.id)} was dropped back in place.`;
      return `${noteTitle(active.id)} was moved to where ${noteTitle(over.id)} was.`;
    },
    onDragCancel: ({ active }) => `Moving ${noteTitle(active.id)} was cancelled.`,
  };

  const reorderNotes = ({ active, over }) => {
    if (!over) return;
    if (active.id === over.id) return;
    moveNote(active.id, over.id);
  };

  const perm = usePermissionSet();
  const fetchPermissionSet = perm.fetchPermissionSet;

  const [view, setView] = useState(() => {
    if (typeof window === "undefined") return "cards";
    return window.localStorage.getItem("sl-notes-view") === "table" ? "table" : "cards";
  });

  const selectView = (next) => {
    setView(next);
    if (typeof window !== "undefined") window.localStorage.setItem("sl-notes-view", next);
  };

  // Date-only label for the table view (human_created_at also carries the time).
  const noteDate = (note) =>
    (note.human_created_at || "").replace(/\s+\d{1,2}:\d{2}\s*[AP]M$/i, "");

  // The per-note overflow menu is shared by both the card and table views.
  const noteMenu = (note) => (
    <Popup
      arrow={false}
      offsetY={5}
      position="bottom right"
      contentStyle={popupContentStyle}
      onOpen={(e) => {
        e.stopPropagation();
        e.preventDefault();
      }}
      trigger={() => (
        <a href="#" className="btn btn-external btn-sm" style={{ borderRadius: "8px" }}>
          <i className="far fa-ellipsis-v"></i>
        </a>
      )}
    >
      {(close) => (
        <ul className="sl-note-menu">
          {canManageNotes ? (
            <li className="sl-note-menu-item">
              <a
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  close();
                  fetchNoteToEdit(csrfToken, team, note.id);
                }}
              >
                <span className="sl-note-menu-item-icon">
                  <i className="fas fa-pencil"></i>
                </span>
                <span>Edit</span>
              </a>
            </li>
          ) : null}
          <li className="sl-note-menu-item">
            <a
              href="#"
              onClick={(e) => {
                e.preventDefault();
                close();
                fetchNoteToPreview(csrfToken, team, note.id);
              }}
            >
              <span className="sl-note-menu-item-icon">
                <i className="fas fa-search"></i>
              </span>
              <span>Preview</span>
            </a>
          </li>
          {canManagePermissions ? (
            <li className="sl-note-menu-item">
              <a
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  close();

                  var permissionSetToFetch;
                  if (note.permission_set_id) {
                    permissionSetToFetch = {
                      id: note.permission_set_id,
                    };
                  } else {
                    permissionSetToFetch = {
                      element_id: note.id,
                      element_type: "Note",
                    };
                  }

                  fetchPermissionSet(team, permissionSetToFetch, (permissionSet) => {
                    var updated = Object.assign({}, note, {
                      permission_set_id: permissionSet.id,
                    });
                    noteUpdated(updated);
                  });
                }}
              >
                <span className="sl-note-menu-item-icon">
                  <i className="far fa-user-lock"></i>
                </span>
                <span>Set Permissions</span>
              </a>
            </li>
          ) : null}
          {canManageNotes ? (
            <li className="sl-note-menu-item sl-note-menu-item--danger">
              <Popup
                trigger={
                  <a href="#">
                    <span className="sl-note-menu-item-icon">
                      <i className="fas fa-trash"></i>
                    </span>
                    <span>Delete</span>
                  </a>
                }
                modal
                onOpen={(e) => {
                  e.preventDefault();
                }}
                contentStyle={deleteModalContentStyle}
                closeOnDocumentClick
              >
                {(closeDeletePopup) => (
                  <div className="sl-note-delete">
                    <div className="sl-note-delete-head">
                      <h5>
                        <strong>Delete note?</strong>
                      </h5>
                      <a
                        href="#"
                        className="sl-note-delete-close"
                        onClick={(e) => {
                          e.preventDefault();
                          close();
                        }}
                      >
                        <i className="fa fa-times"></i>
                      </a>
                    </div>
                    <p>Are you sure you want to remove this note?</p>
                    <div className="sl-note-delete-actions">
                      <a
                        href="#"
                        onClick={(e) => {
                          e.preventDefault();
                          close();
                        }}
                      >
                        Cancel
                      </a>
                      <a
                        href="#"
                        className="sl-btn-danger ml-3"
                        onClick={(e) => {
                          e.preventDefault();
                          close();
                          closeDeletePopup();
                          deleteNote(csrfToken, team, note);
                        }}
                      >
                        Remove
                      </a>
                    </div>
                  </div>
                )}
              </Popup>
            </li>
          ) : null}
        </ul>
      )}
    </Popup>
  );

  if (!canReadNotes) {
    return null;
  }

  return (
    <div className="sl-notes">
      <div className="sl-notes-header">
        <h5 className="sl-notes-title">
          Notes
          {notes.length > 0 ? <span className="sl-notes-count">{notes.length}</span> : null}
          {notesPageSection.permission_set_id ? (
            <i className="ml-2 far fa-lock small sl-note-lock"></i>
          ) : null}
        </h5>
        <div className="sl-notes-header-actions">
          {notes.length > 0 ? (
            <div className="sl-notes-view-toggle" role="group" aria-label="Notes view">
              <button
                type="button"
                className={`sl-notes-view-btn ${view === "cards" ? "active" : ""}`.trim()}
                onClick={() => selectView("cards")}
                title="Card view"
                aria-label="Card view"
              >
                <i className="fas fa-th-large"></i>
              </button>
              <button
                type="button"
                className={`sl-notes-view-btn ${view === "table" ? "active" : ""}`.trim()}
                onClick={() => selectView("table")}
                title="List view"
                aria-label="List view"
              >
                <i className="fas fa-list"></i>
              </button>
            </div>
          ) : null}
          {canManageNotes && notes.length > 0 ? (
            <a
              href="#"
              onClick={(e) => {
                e.preventDefault();
                fetchNewNote(csrfToken, team);
              }}
              className="sl-btn-secondary"
            >
              <strong>New Note</strong>
            </a>
          ) : null}
          {canManagePermissions ? (
            <PageSectionMenu
              csrfToken={csrfToken}
              team={team}
              confirm={confirm}
              currentUser={currentUser}
              confirmPageSection={notesPageSection}
              fetchPermissionSet={fetchPermissionSet}
              updateConfirmPageSection={updateConfirmPageSection}
            />
          ) : null}
        </div>
      </div>
      {notes.length === 0 ? (
        <div className="sl-notes-empty">
          <div className="sl-notes-empty-icon">
            <i className="fal fa-file-alt"></i>
          </div>
          <div className="sl-notes-empty-body">
            <h5>Add a Note</h5>
            <p className="sl-notes-empty-text">
              Notes are a place for you to keep track of event details, to-do lists, and internal communications.
            </p>
            {canManageNotes ? (
              <a
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  fetchNewNote(csrfToken, team);
                }}
                className="sl-btn-primary"
              >
                <strong>New Note</strong>
              </a>
            ) : null}
          </div>
        </div>
      ) : view === "table" ? (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={reorderNotes}
          accessibility={{ announcements: reorderAnnouncements }}
        >
          <SortableContext items={notes.map((note) => note.id)} strategy={verticalListSortingStrategy}>
            <div className="sl-notes-list">
              {notes.map((note) => (
                <SortableNote key={note.id} id={note.id} title={note.title} sortable={canManageNotes}>
                  {({ handle, nodeProps, draggingClassName }) => (
                    <div
                      {...nodeProps}
                      className={`sl-note-row ${draggingClassName}`.trim()}
                      onClick={(e) => {
                        e.preventDefault();
                        fetchNoteToPreview(csrfToken, team, note.id);
                      }}
                    >
                      <div className="sl-note-row-head">
                        {handle}
                        <strong className="sl-note-title">
                          {note.title}
                          {note.permission_set_id ? (
                            <i className="ml-2 far fa-lock small sl-note-lock"></i>
                          ) : null}
                          {note.tags.map((tag) => (
                            <span className="sl-note-tag" key={tag.id}>
                              {tag.name}
                            </span>
                          ))}
                        </strong>
                        <span className="sl-note-row-date">{noteDate(note)}</span>
                        <div className="sl-note-card-menu" onClick={(e) => e.stopPropagation()}>
                          {noteMenu(note)}
                        </div>
                      </div>
                      <div
                        className="sl-note-row-body"
                        dangerouslySetInnerHTML={{ __html: note.sanitized_body }}
                      ></div>
                      {note.note_attachments.length > 0 ? (
                        <div className="sl-note-row-attachments">
                          <i className="far fa-paperclip" />
                          {note.note_attachments.length > 1
                            ? `Linked to ${note.note_attachments.length} offers`
                            : `Linked to ${note.note_attachments[0].label}`}
                        </div>
                      ) : null}
                    </div>
                  )}
                </SortableNote>
              ))}
            </div>
          </SortableContext>
        </DndContext>
      ) : (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={reorderNotes}
          accessibility={{ announcements: reorderAnnouncements }}
        >
          <SortableContext items={notes.map((note) => note.id)} strategy={rectSortingStrategy}>
            <div className="sl-notes-grid">
              {notes.map((note) => (
                <SortableNote key={note.id} id={note.id} title={note.title} sortable={canManageNotes}>
                  {({ handle, nodeProps, draggingClassName }) => (
                    <div {...nodeProps} className={draggingClassName || undefined}>
                      <div
                        onClick={(e) => {
                          e.preventDefault();
                          fetchNoteToPreview(csrfToken, team, note.id);
                        }}
                        className="sl-note-card"
                      >
                        <div className="sl-note-card-body">
                          <div className="sl-note-card-head">
                            {handle}
                            <div className="sl-note-card-head-main">
                              <strong className="sl-note-title">
                                {note.title}
                                {note.permission_set_id ? (
                                  <i className="ml-2 far fa-lock small sl-note-lock"></i>
                                ) : null}
                                {note.tags.map((tag) => (
                                  <span className="sl-note-tag" key={tag.id}>
                                    {tag.name}
                                  </span>
                                ))}
                              </strong>
                            </div>
                            <div className="sl-note-card-menu">{noteMenu(note)}</div>
                          </div>
                          <div
                            className="sl-note-body"
                            dangerouslySetInnerHTML={{ __html: note.sanitized_body }}
                          ></div>
                        </div>
                        {note.note_attachments.length > 0 ? (
                          <div className="sl-note-card-attachments">
                            <i className="far fa-paperclip" />
                            {note.note_attachments.length > 1
                              ? `Linked to ${note.note_attachments.length} offers`
                              : `Linked to ${note.note_attachments[0].label}`}
                          </div>
                        ) : null}
                      </div>
                    </div>
                  )}
                </SortableNote>
              ))}
            </div>
          </SortableContext>
        </DndContext>
      )}
      <Popup
        open={Object.keys(openNote).length > 0}
        contentStyle={modalContentStyle}
        closeOnDocumentClick={false}
        className={"popup-modal"}
        onClose={() => closeOpenNote(csrfToken, team, openNote.id)}
      >
        <div className="popup-modal-container sl-note-modal">
          <form
            onSubmit={(e) => {
              e.preventDefault();

              if (canSubmitForm(openNote, openNoteOriginal, openNoteLoading)) {
                if (openNote.id) {
                  updateNote(csrfToken, team, openNote);
                } else {
                  createNote(csrfToken, team, confirm, openNote);
                }
              }
            }}
          >
            <div className="sl-note-modal-toolbar">
              <div className="sl-note-modal-templates">
                {canManageNoteTemplates && !openNote.previewMode ? (
                  <div className="sl-note-modal-templates-inner">
                    {noteTemplates.length > 0 ? (
                      <Select
                        styles={selectStyles}
                        className="mr-4"
                        placeholder={"Select a template"}
                        components={{ ClearIndicator: () => null }}
                        options={noteTemplates.map((template) => ({
                          value: template.id,
                          label: template.title,
                        }))}
                        value={noteTemplates
                          .map((template) => ({ value: template.id, label: template.title }))
                          .find((template) => template.value === openNote.note_template_id)}
                        formatOptionLabel={(data, meta) => {
                          const noteTemplate = noteTemplates.find(
                            (template) => template.id === data.value
                          );

                          return formatOptionLabel(data, meta, () => {
                            deleteNoteTemplate(csrfToken, team, noteTemplate, () => {
                              openNoteChanged({
                                ...openNote,
                                note_template_id: null,
                              });

                              noteTemplatesChanged([
                                ...noteTemplates.filter(
                                  (template) => template.id !== noteTemplate.id
                                ),
                              ]);

                              toast.success("Note template deleted", {
                                position: toast.POSITION.TOP_CENTER,
                                draggable: false,
                                closeOnClick: true,
                                autoClose: 5000,
                                hideProgressBar: true,
                              });
                            });
                          });
                        }}
                        onChange={(selected) => {
                          const template = noteTemplates.find(
                            (template) => template.id === selected.value
                          );

                          if (template) {
                            openNoteChanged({
                              ...openNote,
                              note_template_id: template.id,
                              title: template.title,
                              body: template.body,
                              editorState: EditorState.createWithContent(
                                richTextContentFromHtml(template.body || "")
                              ),
                            });
                          }
                        }}
                      />
                    ) : null}
                    {openNote.note_template_id ? (
                      <button
                        className="sl-btn-secondary sl-btn-sm"
                        disabled={!canSubmitTemplate(openNote)}
                        onClick={(e) => {
                          e.preventDefault();

                          updateNoteTemplate(csrfToken, team, openNote, (data) => {
                            noteTemplatesChanged(
                              noteTemplates
                                .map((template) => {
                                  if (template.id === data.id) {
                                    return data;
                                  } else {
                                    return template;
                                  }
                                })
                                .sort((a, b) => {
                                  return a.title.localeCompare(b.title);
                                })
                            );

                            toast.success("Note template updated", {
                              position: toast.POSITION.TOP_CENTER,
                              draggable: false,
                              closeOnClick: true,
                              autoClose: 5000,
                              hideProgressBar: true,
                            });
                          });
                        }}
                      >
                        Update Template
                      </button>
                    ) : null}
                    <button
                      className="sl-btn-secondary sl-btn-sm"
                      disabled={!canSubmitTemplate(openNote)}
                      onClick={(e) => {
                        e.preventDefault();

                        createNoteTemplate(csrfToken, team, openNote, (data) => {
                          noteTemplatesChanged(
                            [...noteTemplates, data].sort((a, b) => {
                              return a.title.localeCompare(b.title);
                            })
                          );

                          openNoteChanged({
                            ...openNote,
                            note_template_id: data.id,
                          });

                          toast.success("Note template created", {
                            position: toast.POSITION.TOP_CENTER,
                            draggable: false,
                            closeOnClick: true,
                            autoClose: 5000,
                            hideProgressBar: true,
                          });
                        });
                      }}
                    >
                      Save New Template
                    </button>
                  </div>
                ) : null}
              </div>
              <div className="sl-note-modal-toolbar-actions">
                {openNoteLoading ? (
                  <img
                    className="img-fluid"
                    src="/uploading-loading.gif"
                    style={{ width: "16px" }}
                  />
                ) : null}
                {!openNote.previewMode ? (
                  <button
                    type="submit"
                    disabled={!canSubmitForm(openNote, openNoteOriginal, openNoteLoading)}
                    className="sl-btn-primary ml-2"
                  >
                    Save
                  </button>
                ) : null}
                <a
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    closeOpenNote(csrfToken, team, openNote.id);
                  }}
                  className="sl-note-modal-close ml-4"
                >
                  <i className="fal fa-times"></i>
                </a>
              </div>
            </div>
            <div className="sl-note-modal-content">
              <div className="sl-note-modal-title">
                <input
                  type="text"
                  autoFocus={true}
                  disabled={openNote.previewMode}
                  className="sl-note-modal-title-input"
                  value={openNote.title || ""}
                  onChange={(e) => {
                    openNoteTitleChanged(e.target.value);
                  }}
                  placeholder="Untitled"
                />
              </div>
              <div className="sl-note-modal-meta">
                <table className="sl-note-modal-meta-table">
                  <tbody>
                    <tr>
                      <td>
                        <span className="sl-note-modal-meta-label">Date Created</span>
                      </td>
                      <td>{openNote.human_created_at}</td>
                    </tr>
                    <tr>
                      <td>
                        <span className="sl-note-modal-meta-label">Last Updated</span>
                      </td>
                      <td>{openNote.human_updated_at}</td>
                    </tr>
                    <tr>
                      <td>
                        <span className="sl-note-modal-meta-label">Created By</span>
                      </td>
                      <td>
                        {openNote.team_membership ? openNote.team_membership.name : null}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
              {openNote.id && !openNote.previewMode ? (
                <NoteAttachments
                  csrfToken={csrfToken}
                  team={team}
                  confirm={confirm}
                  currentUser={currentUser}
                  openNote={openNote}
                  openNoteChanged={openNoteChanged}
                />
              ) : null}
              <div className="sl-note-modal-editor">
                {openNote.previewMode ? (
                  <div className="sl-note-modal-preview">
                    {canManageNotes ? (
                      <a
                        href="#"
                        onClick={(e) => {
                          e.preventDefault();
                          disableOpenNotePreviewMode();
                        }}
                        className="sl-btn-secondary sl-note-modal-preview-edit"
                      >
                        <i className="fas fa-pencil"></i>
                      </a>
                    ) : null}
                    <div
                      className="sl-note-modal-preview-body"
                      dangerouslySetInnerHTML={{ __html: openNote.sanitized_body }}
                    ></div>
                  </div>
                ) : (
                  <RichEditor
                    editorState={openNote.editorState}
                    wrapperClassName="manage-notes-editor"
                    editorStyle={{ minHeight: "320px" }}
                    onEditorStateChange={(editorState) => {
                      const noteBody = richTextToHtml(editorState.getCurrentContent());

                      const updated = {
                        ...openNote,
                        body: noteBody,
                        editorState: editorState,
                      };

                      openNoteChanged(updated);
                    }}
                  />
                )}
              </div>
            </div>
          </form>
        </div>
      </Popup>
      <SetPermissionsPopup
        csrfToken={csrfToken}
        team={team}
        confirm={confirm}
        currentUser={currentUser}
        permissionSet={perm.permissionSet}
        permissionSetChanged={perm.permissionSetChanged}
        permissionRuleEnabledClicked={perm.permissionRuleEnabledClicked}
        selectAllPermissionRulesClicked={perm.selectAllPermissionRulesClicked}
        submitPermissionSet={perm.submitPermissionSet}
        onPermissionSetSaved={perm.onPermissionSetSaved}
        guestTeamMemberships={guestTeamMemberships}
        promotersWithPermission={promotersWithPermission}
      />
    </div>
  );
};

Notes.propTypes = {
  initialData: PropTypes.object,
  csrfToken: PropTypes.string.isRequired,
  team: PropTypes.object.isRequired,
  confirm: PropTypes.object.isRequired,
  // Real user object — still required by sub-components (TagManagerApp,
  // SetPermissionsPopup, PageSectionMenu, RichEditor).
  currentUser: PropTypes.object.isRequired,
  // Gates New Note / Edit / Delete / template management.
  canManageNotes: PropTypes.bool,
  // If false, render nothing (parent should also guard).
  canReadNotes: PropTypes.bool,
  // Gates the per-note "Set Permissions" option and the section permission menu.
  canManagePermissions: PropTypes.bool,
  // Gates note template selection / deletion controls inside the note modal.
  canManageNoteTemplates: PropTypes.bool,
  guestTeamMemberships: PropTypes.array,
  promotersWithPermission: PropTypes.array,
  // Optional: called with the current notes array on every change so a parent
  // (e.g. ConfirmShow's Redux store) can stay in sync. No-op when absent.
  onNotesChanged: PropTypes.func,
};

Notes.defaultProps = {
  initialData: {},
  canManageNotes: false,
  canReadNotes: true,
  canManagePermissions: false,
  canManageNoteTemplates: false,
  guestTeamMemberships: [],
  promotersWithPermission: [],
};

export default Notes;
